import type { MapTool, MapClickEvent } from "../../../application/map/tools/types";
import type { ToolDeps } from "./types";
import { parsePolygonId, geoJSONKeyForType } from "../../helpers/JobMap/editHelpers";
import { deletePolygonUseCase } from "../../../application/map/useCases/deletePolygonUseCase";
import { deletePointUseCase } from "../../../application/map/useCases/modifyPointUseCase";

export function makeDeleteTool(deps: ToolDeps): MapTool {
	let jobId: string | null = null;

	return {
		id: "delete",
		label: "Delete",
		cursor: "pointer",
		onActivate() {
			jobId = deps.getSelectedJobId();
			if (!jobId) {
				deps.showError("No job selected");
				deps.deactivateTool();
				return;
			}
		},
		onFeatureClick(featureId: string, _e: MapClickEvent) {
			if (!jobId) return;

			const parsed = parsePolygonId(featureId);
			if (parsed) {
				// Polygon delete (boundary or zones)
				const geoKey = geoJSONKeyForType(parsed.typePrefix);
				if (!geoKey) {
					deps.showError(`Unknown polygon type: ${parsed.typePrefix}`);
					return;
				}

				const fc = geoKey === "boundaryGeoJSON" ? deps.getBoundaryFC(parsed.jobId) : deps.getZonesFC(parsed.jobId);

				if (!fc) {
					deps.showError("No feature collection found");
					return;
				}

				const applyFn =
					geoKey === "boundaryGeoJSON"
						? (newFC: any) => deps.applyBoundary(parsed.jobId, newFC)
						: (newFC: any) => deps.applyZones(parsed.jobId, newFC);

				const result = deletePolygonUseCase(fc, parsed.featureIndex, applyFn, applyFn);
				if (!result.ok) {
					deps.showError(result.error);
					return;
				}

				deps.undoStack.push(result.data.command);
			} else {
				// Point delete — featureId is the sample_id
				const pointsFC = deps.getPointsFC(jobId);
				if (!pointsFC) {
					deps.showError("No points collection");
					return;
				}

				const result = deletePointUseCase(pointsFC, featureId, (col) => deps.applyPoints(jobId!, col));

				if (!result.ok) {
					deps.showError(result.error);
					return;
				}

				deps.undoStack.push(result.data.command);
			}
		},
		onDeactivate() {
			jobId = null;
		},
	};
}
